/**
 * @readonly
 * @enum {number}
 */
const userStates = {
    NOT_LOGGED_IN: 0,
    NOT_SUBSCRIBED: 1,
    SUBSCRIBED: 2,
};

/**
 * @readonly
 */
const acceptedFileTypes = ['csv', 'tsv', 'xlsx'];

/**
 * @readonly
 * react-dropzone v11+ requires `accept` keyed by MIME type (value is the list of
 * file extensions to additionally filter by; empty array = no extension filtering).
 */
const acceptedMimeTypes = {
    'text/csv': [], // csv
    'text/tab-separated-values': [], // tsv
    'text/tsv': [], // tsv
    'text/plain': [],
    'application/vnd.ms-excel': [], // xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [], // xlsx
};

/**
 * @typedef {Object} ReportTypes
 * @property {string} PUBLISHER - 'PUBLISHER'
 * @property {string} PUBLISHING_LABEL - 'PUBLISHING_LABEL'
 * @property {string} TOUCHTUNES - 'TOUCHTUNES'
 * @property {string} DDEX - 'DDEX'
 * @property {string} PUBLISHING_BY_TERRITORY - 'ANUVU' (backend wire-format)
 * @property {string} ROYALTY - 'ROYALTY'
 * @property {string} UNKNOWN - 'UNKNOWN'
 */

/**
 * @type {ReportTypes}
 */
const REPORT_TYPES = {
    PUBLISHER: 'PUBLISHER',
    PUBLISHING_LABEL: 'PUBLISHING_LABEL',
    TOUCHTUNES: 'TOUCHTUNES',
    DDEX: 'DDEX',
    // The user-facing "publishing-by-territory" report. Value is the backend wire-format string.
    PUBLISHING_BY_TERRITORY: 'ANUVU',
    ROYALTY: 'ROYALTY',
    UNKNOWN: 'UNKNOWN',
};

/**
 * Coerce arbitrary input (null, lowercase, unrecognised strings) to a known
 * REPORT_TYPES enum value. Falls back to UNKNOWN.
 */
function normalizeReportType(input) {
    if (input == null) {
        return REPORT_TYPES.UNKNOWN;
    }
    const upper = String(input).toUpperCase();
    return Object.values(REPORT_TYPES).includes(upper) ? upper : REPORT_TYPES.UNKNOWN;
}

/**
 * @typedef {number} ErrorCode
 */

/**
 * @readonly
 * @enum {ErrorCode}
 */
const errorCodes = {
    NO_ERROR: -1,
    NOT_SPECIFIED: 0,
    TIMEOUT: 1,
    FILE_PROCESS_ERRORED: 2,
    UPLOAD_ERROR: 3,
    HEADERS_REQUEST_ERROR: 4,
    HEADERS_UPLOAD_ERROR: 5,
    VALIDATE_HEADERS_ERROR: 6,
    SUBMIT_REPORT_ERROR: 7,
};

/**
 * @typedef {number} LoadingFunctions
 */

/**
 * @readonly
 * @enum {LoadingFunctions}
 */
const loadingFunctions = {
    NOT_LOADING: -1,
    UPLOAD: 0,
    DOWNLOAD_REPORT: 1,
    PROCESSING_REPORT: 2,
};

/**
 * Maps each REPORT_TYPES enum value to the corresponding key in the user's
 * `user_preferences.subscription.drag_and_drop` object. Used by
 * `isSubscribedToReportType` for the per-type subscription check.
 *
 * Wire-format note: the backend exposes publishing-by-territory subscriptions
 * under the legacy `CUSTOM_ANUVU` key, and TouchTunes under `CUSTOM_TOUCHTUNES`.
 */
const REPORT_TYPE_SUBSCRIPTION_KEYS = {
    [REPORT_TYPES.PUBLISHER]: 'PUBLISHER',
    [REPORT_TYPES.PUBLISHING_LABEL]: 'PUBLISHING_LABEL',
    [REPORT_TYPES.PUBLISHING_BY_TERRITORY]: 'CUSTOM_ANUVU',
    [REPORT_TYPES.DDEX]: 'DDEX_DSR',
    [REPORT_TYPES.ROYALTY]: 'ROYALTY_REPORTING',
    [REPORT_TYPES.TOUCHTUNES]: 'CUSTOM_TOUCHTUNES',
};

/**
 * True iff the user has a real subscription for the given report type.
 * The backend guarantees `subscription.id` is set whenever a subscription
 * exists, so checking `.id` is the canonical truth — siblings that check
 * `?.subscription` truthy (e.g. data/reportTypes.js) are equivalent for
 * well-formed responses but slightly looser against malformed shapes.
 */
function isSubscribedToReportType(user, reportType) {
    const key = REPORT_TYPE_SUBSCRIPTION_KEYS[reportType];
    if (!key) {
        return false;
    }
    return !!user?.user_preferences?.subscription?.drag_and_drop?.[key]?.subscription?.id;
}

Object.freeze(userStates);
Object.freeze(acceptedFileTypes);
Object.freeze(REPORT_TYPES);
Object.freeze(REPORT_TYPE_SUBSCRIPTION_KEYS);

export {
    userStates,
    acceptedFileTypes,
    errorCodes,
    loadingFunctions,
    acceptedMimeTypes,
    REPORT_TYPES,
    REPORT_TYPE_SUBSCRIPTION_KEYS,
    isSubscribedToReportType,
    normalizeReportType,
};
